import { ChevronDown, ChevronLeft, ChevronRight, ChevronsUpDown } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';

/**
 * One table, and one pager under it.
 *
 * ## Why lists became tables
 *
 * Cards were fine at ten rows and stopped being fine somewhere around forty.
 * A card spends most of its area on padding and repeats every field label once
 * per row, so comparing two customers means scrolling between them and holding
 * the first in your head. A table puts the same fields in columns, which is the
 * entire point of a column: the eye runs down one and finds the odd one out
 * without reading anything else.
 *
 * The board made it obvious first, because a kanban column is a card list
 * turned on its side, but it was equally true of customers, contracts, areas
 * and the roster.
 *
 * ## Why the pager is not optional
 *
 * Every list in this application used to end in a hard `take:` — 200, 300, 500
 * — with nothing after it. That is not a page, it is a silent truncation: the
 * three hundred and first customer did not appear anywhere and nothing said so.
 * A pager that reports "1,204 in total" is the fix, and the total matters more
 * than the arrows. Without it nobody can tell an empty page from a lost record.
 *
 * ## The horizontal rule this obeys
 *
 * The wrapper scrolls sideways only once the table genuinely does not fit —
 * `min-w-full` means it stretches to the container first. A scrollbar that
 * appears under a table with empty space beside it is the bug that was fixed
 * across this application once already, and re-introducing it inside a shared
 * component would put it back on every screen at once.
 */
export type Column<Row> = {
  /** Stable key, also used for the header cell. */
  key: string;
  header: ReactNode;
  /** Render one cell. Given the row so it can combine fields. */
  cell: (row: Row) => ReactNode;
  /** Numeric columns line up when the digits do. */
  numeric?: boolean;
  /**
   * Hidden below `sm`. For the columns worth losing on a phone — never for the
   * one that identifies the row, or the table becomes a list of blanks.
   */
  secondary?: boolean;
  width?: string;
  /**
   * What this column sorts on. Opt in, and the column becomes sortable.
   *
   * A value rather than a flag, because `cell` returns a `ReactNode` and there
   * is no honest way to order a rendered badge. The value is what the column
   * *means* — a date as a timestamp, a duration in minutes, a priority as its
   * rank — so "oldest first" and "most urgent first" sort the way somebody
   * expects rather than the way the text happens to spell.
   *
   * `null` for a row with nothing to compare. Those sink to the bottom in
   * either direction: an empty cell is not the smallest value, it is the
   * absence of one, and burying it under "ascending" would hide exactly the
   * rows somebody sorting is usually hunting for.
   */
  sortBy?: (row: Row) => string | number | null;
};

export type PageState = {
  page: number;
  perPage: number;
  total: number;
  pages: number;
};

/**
 * How much air a row gets, remembered per person.
 *
 * Comfortable is right for a table somebody reads and wrong for one they
 * scan: the stock list is two hundred rows, and at comfortable spacing a
 * quarter of them fit on a laptop screen. Compact is not a smaller font — the
 * text stays the size it was, only the padding goes, because shrinking the
 * words is how a dense table becomes an unreadable one.
 *
 * In `localStorage` rather than in settings: it is a preference about this
 * person's eyes and this person's screen, not a fact about the business, and
 * it should not follow them onto somebody else's machine.
 */
type Density = 'comfortable' | 'compact';

const DENSITY_KEY = 'gsn-table-density';

/**
 * An empty cell says so, in one voice.
 *
 * Most columns already wrote their own `—` and a few returned nothing, so a
 * table could have both in the same row — and a blank cell is ambiguous in a
 * way a dash is not: it reads as "still loading" or "column broken" as easily
 * as "there is no value here".
 *
 * Done centrally because doing it per column is exactly what produced the
 * inconsistency. A column that wants different wording still returns its own
 * text; this only catches nothing at all.
 */
function withDash(value: ReactNode): ReactNode {
  if (value === null || value === undefined || value === '' || value === false) {
    return <span className="text-muted-foreground">&mdash;</span>;
  }
  return value;
}

function readDensity(): Density {
  try {
    return localStorage.getItem(DENSITY_KEY) === 'compact' ? 'compact' : 'comfortable';
  } catch {
    /* A private window. Comfortable is the safer default to be stuck with. */
    return 'comfortable';
  }
}

export function DataTable<Row>({
  rows,
  columns,
  rowKey,
  onRowClick,
  empty,
  page,
  onPage,
  caption,
  defaultSort,
  selected,
  onSelect,
}: {
  rows: Row[];
  columns: Column<Row>[];
  rowKey: (row: Row) => string;
  /** Makes the whole row activate. Keyboard included — see below. */
  onRowClick?: (row: Row) => void;
  empty: ReactNode;
  page?: PageState;
  onPage?: (page: number) => void;
  /** Announced to screen readers, and the visible count line. */
  caption?: ReactNode;
  /** Which column to sort by before anybody touches a header. */
  defaultSort?: { key: string; direction?: 'asc' | 'desc' };
  /**
   * The rows ticked, and a way to change them. Omit both and no checkbox
   * column is drawn at all — a table nobody can act on should not spend a
   * column saying so.
   *
   * Held by the caller rather than here, because the thing that acts on a
   * selection lives there: the page knows what "archive these" means and this
   * component never should.
   */
  selected?: Set<string>;
  onSelect?: (ids: Set<string>) => void;
}) {
  const [density, setDensity] = useState<Density>(readDensity);

  const [sort, setSort] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(
    defaultSort ? { key: defaultSort.key, direction: defaultSort.direction ?? 'asc' } : null,
  );

  const sortable = new Map(
    columns.filter((column) => column.sortBy).map((column) => [column.key, column.sortBy!]),
  );

  const sorted = useMemo(() => {
    const pick = sort ? sortable.get(sort.key) : undefined;
    if (!sort || !pick) return rows;

    const factor = sort.direction === 'asc' ? 1 : -1;

    return [...rows].sort((a, b) => {
      const left = pick(a);
      const right = pick(b);

      /*
       * Absent values sink, whichever way the column is pointing.
       *
       * Sorting by "last paid" to find who has never paid, and having those
       * rows sort to the bottom under ascending because null reads as zero, is
       * the exact failure this avoids — the rows somebody is hunting for are
       * the ones with nothing in the cell.
       */
      if (left === null && right === null) return 0;
      if (left === null) return 1;
      if (right === null) return -1;

      if (typeof left === 'number' && typeof right === 'number') {
        return (left - right) * factor;
      }

      /*
       * `localeCompare` with `numeric`, so JOB-9 comes before JOB-10 rather
       * than after it. Plain string order is why every reference list in every
       * application reads 1, 10, 11, 2.
       */
      return String(left).localeCompare(String(right), 'en', { numeric: true }) * factor;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort, columns]);

  /*
   * Selection is offered only when somebody is holding the other end of it.
   * `onSelect` without `selected` would be a checkbox that ticks and forgets.
   */
  const selectable = Boolean(selected && onSelect);
  const chosen = selected ?? new Set<string>();

  const visibleIds = sorted.map((row) => rowKey(row));
  const allChosen = visibleIds.length > 0 && visibleIds.every((id) => chosen.has(id));
  const someChosen = visibleIds.some((id) => chosen.has(id));

  const setChosen = (next: Set<string>) => onSelect?.(next);

  const toggleRow = (id: string) => {
    const next = new Set(chosen);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setChosen(next);
  };

  /*
   * The header box ticks and unticks what is on screen, and leaves anything
   * chosen on another page alone. Somebody who ticked three rows on page one,
   * paged forward and pressed "all" means "and these too" — clearing the first
   * three would be a silent loss they only find out about after pressing
   * Archive.
   */
  const toggleVisible = () => {
    const next = new Set(chosen);
    if (allChosen) for (const id of visibleIds) next.delete(id);
    else for (const id of visibleIds) next.add(id);
    setChosen(next);
  };

  const toggle = (key: string) =>
    setSort((current) =>
      current?.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' },
    );

  if (rows.length === 0) return <>{empty}</>;

  /* Padding only. The text stays the size it was — see `Density`. */
  const pad = density === 'compact' ? 'px-3 py-1' : 'px-3 py-2';

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button
          type="button"
          className="btn-quiet px-2 py-1 text-xs"
          aria-pressed={density === 'compact'}
          title={
            density === 'compact'
              ? 'More room between rows'
              : 'Fit more rows on the screen. The text stays the same size.'
          }
          onClick={() => {
            const next: Density = density === 'compact' ? 'comfortable' : 'compact';
            setDensity(next);
            try {
              localStorage.setItem(DENSITY_KEY, next);
            } catch {
              /* A private window. It simply is not remembered. */
            }
          }}
        >
          {density === 'compact' ? 'Comfortable' : 'Compact'}
        </button>
      </div>

      {/*
        `max-h` plus `overflow-y-auto` is what makes a sticky header stick.
        `position: sticky` needs a scrolling ancestor, and without one the
        header is sticky to a box that never scrolls, which looks exactly like
        it not working.

        The height is generous on purpose: short tables never reach it and
        behave as they always did, and long ones get their own scroll rather
        than taking the whole page with them.
      */}
      <div className="max-h-[70vh] overflow-auto rounded-2xl border border-border">
        <table className="min-w-full divide-y divide-border text-sm">
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead className="sticky top-0 z-10 bg-muted/50 backdrop-blur">
            <tr>
              {selectable && (
                <th scope="col" className={`w-10 ${pad}`}>
                  <input
                    type="checkbox"
                    className="h-4 w-4 cursor-pointer align-middle"
                    checked={allChosen}
                    /*
                     * Half-ticked when some but not all are chosen. There is
                     * no HTML attribute for it — it has to be set on the node,
                     * which is what this ref does.
                     */
                    ref={(node) => {
                      if (node) node.indeterminate = someChosen && !allChosen;
                    }}
                    onChange={toggleVisible}
                    aria-label={allChosen ? 'Clear this page' : 'Select everything on this page'}
                  />
                </th>
              )}
              {columns.map((column) => {
                const canSort = sortable.has(column.key);
                const active = sort?.key === column.key;

                return (
                  <th
                    key={column.key}
                    scope="col"
                    style={column.width ? { width: column.width } : undefined}
                    /*
                     * `aria-sort` on the header is what tells a screen reader
                     * the table is ordered and which way. Without it the
                     * arrows are decoration that only sighted users can read.
                     */
                    aria-sort={
                      active ? (sort!.direction === 'asc' ? 'ascending' : 'descending') : undefined
                    }
                    className={`${pad} text-left font-medium text-muted-foreground ${
                      column.numeric ? 'text-right tabular-nums' : ''
                    } ${column.secondary ? 'hidden sm:table-cell' : ''}`}
                  >
                    {canSort ? (
                      <button
                        type="button"
                        onClick={() => toggle(column.key)}
                        className={`-mx-1 flex items-center gap-1 rounded px-1 py-0.5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                          column.numeric ? 'ml-auto flex-row-reverse' : ''
                        } ${active ? 'text-foreground' : ''}`}
                      >
                        {column.header}
                        {active ? (
                          <ChevronDown
                            className={`h-3.5 w-3.5 shrink-0 transition-transform ${
                              sort!.direction === 'asc' ? 'rotate-180' : ''
                            }`}
                            aria-hidden="true"
                          />
                        ) : (
                          /* Shown faintly on every sortable column, so which
                             ones can be sorted is visible before hovering. */
                          <ChevronsUpDown
                            className="h-3.5 w-3.5 shrink-0 opacity-40"
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sorted.map((row) => (
              <tr
                key={rowKey(row)}
                /*
                 * A clickable row needs to be reachable without a mouse. `tabIndex`
                 * and the key handler are what make it so; without them the whole
                 * table is invisible to anybody navigating by keyboard, which on a
                 * dispatch screen somebody uses all day is not a small thing.
                 */
                {...(onRowClick
                  ? {
                      tabIndex: 0,
                      role: 'button',
                      onClick: () => onRowClick(row),
                      onKeyDown: (event: React.KeyboardEvent) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onRowClick(row);
                        }
                      },
                      className:
                        'cursor-pointer transition-colors hover:bg-muted/40 focus:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset',
                    }
                  : {})}
              >
                {selectable && (
                  <td
                    className={`w-10 ${pad} align-middle`}
                    /*
                     * The click stops here. Every row on a clickable table
                     * navigates, and ticking a box to choose a ticket should
                     * not also open it — which is what happened before this,
                     * and made selection unusable on exactly the tables that
                     * most needed it.
                     */
                    onClick={(event) => event.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 cursor-pointer align-middle"
                      checked={chosen.has(rowKey(row))}
                      onChange={() => toggleRow(rowKey(row))}
                      aria-label={`Select this row`}
                    />
                  </td>
                )}
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={`${pad} align-middle ${
                      column.numeric ? 'text-right tabular-nums' : ''
                    } ${column.secondary ? 'hidden sm:table-cell' : ''}`}
                  >
                    {withDash(column.cell(row))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/*
        * Said out loud when it matters.
        *
        * The rows here are one page of a larger set, so sorting orders the
        * twenty-five in front of you and not the twelve hundred behind them.
        * That is a perfectly useful thing to do and a terrible thing to
        * misunderstand — somebody sorting by age to find the oldest ticket
        * would otherwise be shown the oldest on page one and have no idea.
        */}
      {sort && page && page.pages > 1 && (
        <p className="text-xs text-accent-700">
          Sorted within this page — {page.total.toLocaleString('en-KE')} in total across{' '}
          {page.pages} pages.
        </p>
      )}

      {page && onPage && <Pager page={page} onPage={onPage} />}
    </div>
  );
}

/**
 * The pager.
 *
 * Says the total before it says anything else. "Showing 1–25 of 1,204" answers
 * the question somebody actually has — is what I am looking for even in here —
 * which the arrows alone never can.
 */
export function Pager({ page, onPage }: { page: PageState; onPage: (page: number) => void }) {
  const first = page.total === 0 ? 0 : (page.page - 1) * page.perPage + 1;
  const last = Math.min(page.page * page.perPage, page.total);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-xs text-muted-foreground" aria-live="polite">
        Showing <span className="tabular-nums">{first.toLocaleString('en-KE')}</span>–
        <span className="tabular-nums">{last.toLocaleString('en-KE')}</span> of{' '}
        <span className="tabular-nums font-medium text-foreground">
          {page.total.toLocaleString('en-KE')}
        </span>
      </p>

      {page.pages > 1 && (
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="btn-quiet text-xs"
            title={page.page <= 1 ? 'You are on the first page already' : 'The page before this'}
            disabled={page.page <= 1}
            onClick={() => onPage(page.page - 1)}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            Back
          </button>

          <span className="px-2 text-xs tabular-nums text-muted-foreground">
            {page.page} of {page.pages}
          </span>

          <button
            type="button"
            className="btn-quiet text-xs"
            title={
              page.page >= page.pages ? 'You are on the last page already' : 'The next page'
            }
            disabled={page.page >= page.pages}
            onClick={() => onPage(page.page + 1)}
          >
            Next
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}
